import React from 'react';
import { useTranslation } from 'react-i18next';

import { TimePicker } from '@/components/ui/inputs/TimePicker';
import { TextArea } from '@/components/ui/inputs/TextArea';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { Text } from '@/components/ui/text/Text';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useBulkAdjustMarkForm } from '@/hooks/attendance/report/useBulkAdjustMarkForm';
import { markingEventService } from '@/api/domains/catalog/eventType/eventType.service';
import { colors } from '@/assets/styles';
import type { MarkReport } from '@/api/domains/attendance/types/marksReport.types';


const COMMENT_MAX_LENGTH = 500;
const COMMENT_TEXTAREA_ROWS = 3;

export interface BulkAdjustMarkPanelProps {
  records: MarkReport[];
  onSubmit?: () => void;
}

export const BulkAdjustMarkPanel: React.FC<BulkAdjustMarkPanelProps> = ({
  records,
  onSubmit,
}) => {
  const { t: translate } = useTranslation();

  const { options: eventOptions } = useCatalogLoader(
    (signal) => markingEventService.getMarkingEvents(signal),
    (event) => ({ value: String(event.id), label: event.description }),
  );

  const {
    isSubmitting,
    isReady,
    handleSubmit,
    errors,
    isSubmitted,
    newTime,
    newEventId,
    comment,
    handleTimeChange,
    handleTimePickerOpenChange,
    handleEventChange,
    handleCommentChange,
  } = useBulkAdjustMarkForm({ records, onSubmit });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="bulk-adjust-mark-panel">
      <SidePanelFormLayout.Fields>
        <Text variant="body-medium" color={colors.grays.neutral66}>
          {translate('attendance:marksReport.adjustBulk.selectedCount', { count: records.length })}
        </Text>

        <TimePicker
          label={translate('attendance:marksReport.adjustBulk.fields.newTime')}
          value={newTime}
          onChange={handleTimeChange}
          onOpenChange={handleTimePickerOpenChange}
          disabled={isSubmitting}
          placeholder={translate('attendance:marksReport.adjustBulk.placeholders.newTime')}
          color={errors.newTime && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.newTime?.message && isSubmitted
              ? translate(errors.newTime.message)
              : undefined
          }
        />

        <Selector
          label={translate('attendance:marksReport.adjustBulk.fields.newEvent')}
          placeholder={translate('attendance:marksReport.adjustBulk.placeholders.newEvent')}
          options={eventOptions}
          value={newEventId}
          onChange={(value) => handleEventChange(Array.isArray(value) ? value[0] ?? '' : value)}
          disabled={isSubmitting}
          color={errors?.newEventId && isSubmitted ? 'error' : 'default'}
        />

        <TextArea
          label={translate('attendance:marksReport.adjustBulk.fields.comment')}
          placeholder={translate('attendance:marksReport.adjustBulk.placeholders.comment')}
          value={comment}
          onChange={handleCommentChange}
          disabled={isSubmitting}
          maxLength={COMMENT_MAX_LENGTH}
          required
          rows={COMMENT_TEXTAREA_ROWS}
          color={errors.comment && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.comment?.message && isSubmitted
              ? translate(errors.comment.message)
              : undefined
          }
          showCharacterCount
        />
      </SidePanelFormLayout.Fields>

      <PermissionGuard action="edit">
        <SidePanelFormLayout.Footer>
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={!isReady || isSubmitting}
          >
            {translate('attendance:marksReport.adjustBulk.save')}
          </PrimaryButton>
        </SidePanelFormLayout.Footer>
      </PermissionGuard>
    </SidePanelFormLayout>
  );
};

export default BulkAdjustMarkPanel;
